"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  resetHotelSessionState,
  setHotelExpiredTimer,
} from "@/feature/hotel/checkout/state/hotel-data.slice";
import { useSearchServiceFlow } from "@/feature/umrah/custom/request/hooks/use-search-service-flow";
import { requestHotelFilterResetOnNextSearch } from "@/feature/hotel/search/state/extract-hotel-filter-slice";
import { baseApi } from "@/redux/api/base-api";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { ArrowLeft, Clock, RefreshCw, Timer } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";

export function HotelSearchTimer() {
  const router = useRouter();
  const pathname = usePathname();
  const { source, umrahCity } = useSearchServiceFlow();
  const dispatch = useAppDispatch();

  const { hotelTimer, isLoading } = useAppSelector(
    (state) => state.selectedHotelData,
  );

  const [timeLeft, setTimeLeft] = useState<number | null>(() => {
    if (!hotelTimer) return null;

    return Math.max(hotelTimer - Date.now(), 0);
  });
  const [showDialog, setShowDialog] = useState(false);

  const [isPending, startTransition] = useTransition();

  // Track if user clicked "Search Again"
  const closeAfterPendingRef = useRef(false);
  const timerAlreadyExpiredOnMountRef = useRef(false);
  const isRevalidatePage = pathname.startsWith("/hotels/");

  useEffect(() => {
    if (!hotelTimer) {
      timerAlreadyExpiredOnMountRef.current = false;
      return;
    }

    if (hotelTimer - Date.now() <= 0) {
      timerAlreadyExpiredOnMountRef.current = true;

      const frameId = window.requestAnimationFrame(() => {
        setTimeLeft(0);

        if (!isRevalidatePage) {
          dispatch(setHotelExpiredTimer(undefined));
        } else if (!isLoading) {
          setShowDialog(true);
        }
      });

      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }

    timerAlreadyExpiredOnMountRef.current = false;

    const tick = () => {
      const difference = hotelTimer - Date.now();

      if (difference <= 0) {
        setTimeLeft(0);

        if (!timerAlreadyExpiredOnMountRef.current) {
          if (isRevalidatePage || !isLoading) {
            setShowDialog(true);
          }
        }
        return false;
      }

      setTimeLeft(difference);
      return true;
    };

    const interval = setInterval(() => {
      if (!tick()) clearInterval(interval);
    }, 1000);

    const frameId = window.requestAnimationFrame(() => {
      if (!tick()) clearInterval(interval);
    });

    return () => {
      clearInterval(interval);
      window.cancelAnimationFrame(frameId);
    };
  }, [dispatch, hotelTimer, isLoading, isRevalidatePage]);

  // Close dialog when transition finishes
  useEffect(() => {
    if (!closeAfterPendingRef.current || isPending) return;
    closeAfterPendingRef.current = false;
    window.requestAnimationFrame(() => {
      setShowDialog(false);
    });
  }, [isPending]);

  const handleAction = useCallback(() => {
    closeAfterPendingRef.current = true;

    startTransition(() => {
      dispatch(requestHotelFilterResetOnNextSearch());
      dispatch(baseApi.util.invalidateTags(["HOTEL_SEARCH"]));

      if (isRevalidatePage) {
        const nextSearchParams = new URLSearchParams(window.location.search);

        if (source) {
          nextSearchParams.set("source", source);
        }

        if (umrahCity) {
          nextSearchParams.set("umrah_city", umrahCity);
        }

        router.push(`/hotels?${nextSearchParams.toString()}`, { scroll: false });
      }
      if (!isRevalidatePage) {
        dispatch(resetHotelSessionState());
      }
    });
  }, [dispatch, isRevalidatePage, router, source, umrahCity]);

  if (!hotelTimer || timeLeft === null) return null;

  const isExpired = timeLeft <= 0;

  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);
  const formattedTime = `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;

  return (
    <>
      {!isExpired && (isRevalidatePage || !isLoading) && (
        <div className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground">
          <Timer className="h-3.5 w-3.5" />
          <span className="tabular-nums">{formattedTime}</span>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          showCloseButton={false}
        >
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300">
                <Clock className="size-5" />
              </div>

              <DialogHeader className=" text-left">
                <DialogTitle className="text-lg font-semibold leading-tight">
                  {isRevalidatePage
                    ? "Search Session Expired"
                    : "Hotel Search Expired"}
                </DialogTitle>
                <DialogDescription className="text-sm leading-6 text-muted-foreground">
                  {isRevalidatePage
                    ? "This hotel session is no longer valid. Availability or pricing may have changed. Please return to search results and run the search again."
                    : "Your hotel search results have expired. Refresh the search to load the latest hotel availability and prices."}
                </DialogDescription>
              </DialogHeader>
            </div>

            {isRevalidatePage ? (
              <div className="rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5 text-sm leading-5 text-muted-foreground">
                Room selection, traveler details, and payment are locked until a
                fresh hotel search is loaded.
              </div>
            ) : null}

            <div className="flex w-full justify-end">
              <Button
                className="h-10 w-full gap-2 sm:w-auto"
                onClick={handleAction}
                disabled={isPending}
              >
                {isPending ? (
                  isRevalidatePage ? (
                    <>
                      <ArrowLeft className="size-4 animate-pulse" />
                      Going back...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="size-4 animate-spin" />
                      Refreshing...
                    </>
                  )
                ) : isRevalidatePage ? (
                  <>
                    <ArrowLeft className="size-4" />
                    Back to Search Results
                  </>
                ) : (
                  <>
                    <RefreshCw className="size-4" />
                    Refresh Search
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}



